/**
 * @fileoverview NotificationItem — one row in the notifications popover.
 *
 * Foundry mapping: `notification-components__container`. Layout is a fixed
 * status column (intent-colored Blueprint Tag wrapping the type icon) beside a
 * flexible main column: body → optional action → relative timestamp with a
 * hover-revealed "copy id" button. The `is-unread` modifier tints rows the
 * user hasn't acknowledged yet.
 */

import { Button, Icon, Tag, Tooltip } from "@blueprintjs/core";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import type { Notification } from "./notifications-data";

export function NotificationItem({
  notification: n,
  unread = false,
}: {
  notification: Notification;
  unread?: boolean;
}) {
  // Brief click feedback: the copy button flips to a green tick + "Copied!"
  // tooltip for ~1.5s so the user sees the action registered.
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (resetTimer.current) clearTimeout(resetTimer.current); }, []);

  const onCopy = () => {
    try {
      void navigator.clipboard?.writeText(n.id);
    } catch {
      // clipboard unavailable (insecure context / permissions) — no-op
    }
    setCopied(true);
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={`bp-notification${unread ? " is-unread" : ""}`}>
      <div className="bp-notification-status">
        <Tag minimal intent={n.intent} className="bp-notification-chip" aria-hidden>
          <Icon icon={n.icon} size={14} />
        </Tag>
      </div>

      <div className="bp-notification-main">
        <div className="bp-notification-body">
          {n.title ? <div className="bp-notification-title">{n.title}</div> : null}
          {n.body}
        </div>

        {n.action ? (
          <div className="bp-notification-actions">
            <Link
              to={n.action.href}
              className="bp6-button bp6-small bp6-intent-primary bp-notification-action"
            >
              <span className="bp6-button-text">{n.action.text}</span>
            </Link>
          </div>
        ) : null}

        <div className="bp-notification-time bp6-text-muted">
          <span>{n.time}</span>
          <Tooltip
            content={copied ? "Copied!" : "Copy notification ID"}
            placement="top"
            compact
            inheritDarkTheme={false}
          >
            <Button
              minimal
              small
              icon={copied ? "tick" : "clipboard"}
              intent={copied ? "success" : "none"}
              aria-label={copied ? "Notification ID copied" : "Copy notification ID"}
              className={`bp-notification-copy${copied ? " is-copied" : ""}`}
              onClick={onCopy}
            />
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
